import { useEffect, useMemo, useRef, useState } from 'react';
import GearPicker from './GearPicker.jsx';
import PlacePicker from './PlacePicker.jsx';
import SeatStrip from './SeatStrip.jsx';
import { fromLocalInput, initials, toLocalInput } from '../lib/format.js';

const STOP_KINDS = [
  ['pickup', 'Ramassage de personnes'],
  ['club', 'Passage au club'],
  ['gear', 'Recuperation de materiel'],
  ['fuel', 'Essence'],
  ['food', 'Pause / casse-croute'],
  ['other', 'Autre arret'],
];

const emptyStop = () => ({
  key: Math.random().toString(36).slice(2),
  id: null,
  kind: 'pickup',
  place: null,
  time: '',
  pickupCount: 0,
  note: '',
});

function fromRide(ride) {
  if (!ride) return null;
  return {
    driverPhone: ride.driver.phone ?? '',
    model: ride.car.model ?? '',
    colour: ride.car.colour ?? '',
    totalSeats: ride.car.totalSeats,
    seatsOffered: ride.car.seatsOffered,
    origin: ride.origin,
    departureTime: toLocalInput(ride.departureTime),
    returnTime: toLocalInput(ride.returnTime),
    gear: ride.gear ?? [],
    notes: ride.notes ?? '',
    status: ride.status,
    stops: (ride.stops ?? []).map((s) => ({
      key: s.id,
      id: s.id,
      kind: s.kind,
      place: s.place,
      time: toLocalInput(s.time),
      pickupCount: s.pickupCount ?? 0,
      note: s.note ?? '',
    })),
  };
}

export default function RideForm({
  ride = null,
  me,
  defaultDeparture,
  gearSuggestions,
  mapContext,
  tiles,
  onDraftChange,
  onSubmit,
  onCancel,
  busy,
  error,
}) {
  const [form, setForm] = useState(
    () =>
      fromRide(ride) ?? {
        driverPhone: me?.phone ?? '',
        model: '',
        colour: '',
        totalSeats: 5,
        seatsOffered: 4,
        origin: null,
        departureTime: toLocalInput(defaultDeparture),
        returnTime: '',
        gear: [],
        notes: '',
        status: 'open',
        stops: [],
      },
  );

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const setStop = (key, patch) =>
    setForm((f) => ({ ...f, stops: f.stops.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));

  /* Let the map draw the itinerary as it is being built. */
  useEffect(() => {
    onDraftChange?.({
      origin: form.origin,
      stops: form.stops.map((s) => s.place).filter(Boolean),
    });
  }, [form.origin, form.stops, onDraftChange]);

  useEffect(() => () => onDraftChange?.(null), [onDraftChange]);

  // The driver always occupies one seat, so the offer can never reach the total.
  const maxOfferable = Math.max(0, form.totalSeats - 1);
  const minOfferable = ride?.seats.seatsTaken ?? 0;

  const changeTotal = (total) =>
    set({
      totalSeats: total,
      // Keep the offer inside [already booked, seats minus the driver].
      seatsOffered: Math.max(minOfferable, Math.min(form.seatsOffered, Math.max(0, total - 1))),
    });

  const seatPreview = useMemo(
    () => ({
      totalSeats: form.totalSeats,
      seatsOffered: Math.min(form.seatsOffered, maxOfferable),
      seatsTaken: minOfferable,
      seatsFree: Math.max(0, Math.min(form.seatsOffered, maxOfferable) - minOfferable),
    }),
    [form.totalSeats, form.seatsOffered, maxOfferable, minOfferable],
  );

  const incompleteStop = form.stops.some((s) => !s.place);
  const canSubmit = form.origin && form.departureTime && !incompleteStop && !busy;

  const submit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      driverPhone: form.driverPhone.trim(),
      car: {
        model: form.model.trim(),
        colour: form.colour.trim(),
        totalSeats: form.totalSeats,
        seatsOffered: Math.min(form.seatsOffered, maxOfferable),
      },
      origin: form.origin,
      departureTime: fromLocalInput(form.departureTime),
      returnTime: fromLocalInput(form.returnTime),
      status: form.status,
      gear: form.gear,
      notes: form.notes.trim(),
      stops: form.stops
        .filter((s) => s.place)
        .map((s) => ({
          id: s.id ?? undefined,
          kind: s.kind,
          place: s.place,
          time: fromLocalInput(s.time),
          pickupCount: Number(s.pickupCount) || 0,
          note: s.note.trim(),
        })),
    });
  };

  return (
    <form onSubmit={submit} id="ride-form" className="stack gap-16">
      {error && <div className="toast error" style={{ borderRadius: 10 }}>⚠ <span>{error}</span></div>}

      <fieldset className="fieldset stack gap-12">
        <legend>Conducteur</legend>
        {/* No name box: the outing already knows who you are, and asking again
            is how the same person ends up in the roster twice. */}
        <div className="row gap-10">
          <span className="whoami-avatar" style={{ width: 30, height: 30, fontSize: 12 }}>
            {initials(me?.name)}
          </span>
          <span className="small" style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontWeight: 600 }}>{me?.name}</span>
            <span className="faint">Vous proposez cette voiture sous ce nom.</span>
          </span>
        </div>
        <div className="field">
          <label htmlFor="rf-phone">Telephone</label>
          <input
            id="rf-phone"
            className="input"
            type="tel"
            value={form.driverPhone}
            onChange={(e) => set({ driverPhone: e.target.value })}
            placeholder="06 12 34 56 78"
          />
          <span className="field-hint">Visible par les passagers de votre voiture.</span>
        </div>
      </fieldset>

      <fieldset className="fieldset stack gap-12">
        <legend>Voiture et places</legend>
        <div className="field-row">
          <div className="field">
            <label htmlFor="rf-model">Modele</label>
            <input
              id="rf-model"
              className="input"
              value={form.model}
              onChange={(e) => set({ model: e.target.value })}
              placeholder="Kangoo, Berlingo..."
            />
          </div>
          <div className="field">
            <label htmlFor="rf-colour">Couleur</label>
            <input
              id="rf-colour"
              className="input"
              value={form.colour}
              onChange={(e) => set({ colour: e.target.value })}
              placeholder="Blanc"
            />
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label htmlFor="rf-total">Places dans la voiture</label>
            <select
              id="rf-total"
              className="select"
              value={form.totalSeats}
              onChange={(e) => changeTotal(Number(e.target.value))}
            >
              {[2, 3, 4, 5, 6, 7, 8, 9]
                // Never offer a vehicle too small for the people already booked.
                .filter((n) => n - 1 >= minOfferable)
                .map((n) => (
                  <option key={n} value={n}>{n} places (conducteur inclus)</option>
                ))}
            </select>
            <span className="field-hint">Total du vehicule, votre siege compris.</span>
          </div>
          <div className="field">
            <label htmlFor="rf-offered">Places proposees</label>
            <select
              id="rf-offered"
              className="select"
              value={Math.min(form.seatsOffered, maxOfferable)}
              onChange={(e) => set({ seatsOffered: Number(e.target.value) })}
            >
              {Array.from({ length: maxOfferable + 1 }, (_, n) => n)
                .filter((n) => n >= minOfferable)
                .map((n) => (
                  <option key={n} value={n}>{n} passager{n > 1 ? 's' : ''}</option>
                ))}
            </select>
            <span className="field-hint">
              {maxOfferable} maximum. Reduisez si du materiel occupe un siege.
            </span>
          </div>
        </div>

        <div style={{ padding: '2px 0' }}>
          <SeatStrip
            seats={seatPreview}
            driverName={me?.name || 'Vous'}
            passengers={ride?.passengers ?? []}
            color="var(--accent)"
            variant="detailed"
          />
        </div>
        {minOfferable > 0 && (
          <p className="field-hint">
            {minOfferable} personne(s) sont deja inscrites, vous ne pouvez pas descendre en dessous.
          </p>
        )}
      </fieldset>

      <fieldset className="fieldset stack gap-12">
        <legend>Depart</legend>
        <PlacePicker
          label="D'ou partez-vous"
          required
          value={form.origin}
          onChange={(place) => set({ origin: place })}
          contextMarkers={mapContext}
          tiles={tiles}
          hint="Adresse, ou un point place directement sur la carte."
        />
        <div className="field-row">
          <div className="field">
            <label htmlFor="rf-dep">Heure de depart *</label>
            <input
              id="rf-dep"
              className="input"
              type="datetime-local"
              value={form.departureTime}
              onChange={(e) => set({ departureTime: e.target.value })}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="rf-ret">Retour prevu</label>
            <input
              id="rf-ret"
              className="input"
              type="datetime-local"
              value={form.returnTime}
              onChange={(e) => set({ returnTime: e.target.value })}
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="fieldset stack gap-10">
        <legend>Arrets en chemin</legend>
        {form.stops.length === 0 && (
          <p className="field-hint" style={{ margin: 0 }}>
            Aucun arret. Ajoutez-en pour passer au club, recuperer du materiel ou charger quelqu'un en route.
          </p>
        )}

        {form.stops.map((stop, index) => (
          <div className="stop-card stack gap-10" key={stop.key}>
            <div className="row gap-8">
              <span className="stop-index">{index + 1}</span>
              <select
                className="select"
                style={{ flex: 1 }}
                value={stop.kind}
                onChange={(e) => setStop(stop.key, { kind: e.target.value })}
                aria-label={`Type de l'arret ${index + 1}`}
              >
                {STOP_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => set({ stops: form.stops.filter((s) => s.key !== stop.key) })}
                aria-label={`Supprimer l'arret ${index + 1}`}
              >
                ✕
              </button>
            </div>

            <PlacePicker
              value={stop.place}
              onChange={(place) => setStop(stop.key, { place })}
              placeholder="Ou vous arretez-vous ?"
              contextMarkers={mapContext}
              tiles={tiles}
            />

            <div className="field-row">
              <div className="field">
                <label htmlFor={`stop-time-${stop.key}`}>Heure</label>
                <input
                  id={`stop-time-${stop.key}`}
                  className="input"
                  type="datetime-local"
                  value={stop.time}
                  onChange={(e) => setStop(stop.key, { time: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor={`stop-pax-${stop.key}`}>Personnes chargees</label>
                <input
                  id={`stop-pax-${stop.key}`}
                  className="input"
                  type="number"
                  min={0}
                  max={8}
                  value={stop.pickupCount}
                  onChange={(e) => setStop(stop.key, { pickupCount: e.target.value })}
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor={`stop-note-${stop.key}`}>Precision</label>
              <input
                id={`stop-note-${stop.key}`}
                className="input"
                value={stop.note}
                onChange={(e) => setStop(stop.key, { note: e.target.value })}
                placeholder="Devant la boulangerie, parking du haut..."
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-sm"
          onClick={() => set({ stops: [...form.stops, emptyStop()] })}
          disabled={form.stops.length >= 12}
        >
          + Ajouter un arret
        </button>
      </fieldset>

      <fieldset className="fieldset stack gap-12">
        <legend>Materiel et infos</legend>
        <GearPicker
          value={form.gear}
          onChange={(gear) => set({ gear })}
          suggestions={gearSuggestions}
          label="Ce que vous emmenez"
        />
        <div className="field">
          <label htmlFor="rf-notes">Note pour le groupe</label>
          <textarea
            id="rf-notes"
            className="textarea"
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
            placeholder="Je peux faire un detour, merci de participer au peage, chien a bord..."
            maxLength={600}
          />
        </div>
        {ride && (
          <div className="field">
            <label htmlFor="rf-status">Statut du trajet</label>
            <select
              id="rf-status"
              className="select"
              value={form.status}
              onChange={(e) => set({ status: e.target.value })}
            >
              <option value="open">Ouvert aux passagers</option>
              <option value="closed">Ferme (plus d'inscriptions)</option>
              <option value="cancelled">Annule</option>
            </select>
          </div>
        )}
      </fieldset>

      <div className="row gap-10">
        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          {busy && <span className="spinner" />}
          {ride ? 'Enregistrer' : 'Proposer ma voiture'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Annuler</button>
        {incompleteStop && <span className="small faint">Chaque arret a besoin d'un lieu.</span>}
      </div>
    </form>
  );
}
